import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Play } from "lucide-react";
import { HeroSection } from "./roofing/hero";
import { CTA_COPY } from "./roofing/hero/heroContent";
import { CaseStudySection } from "./roofing/caseStudy";
import { EconomicsSection } from "./roofing/economics";
import { SecretarySection } from "./roofing/digitalSecretary";
import { CustomerExperienceBand } from "./roofing/customerExperience";
import { EmployeeDutiesSection } from "./roofing/employeeDuties";
import { ControlSection } from "./roofing/control";
import { GuardrailsSection } from "./roofing/guardrails";
import { FaqSection } from "./roofing/faq";
import { FinalCtaSection } from "./roofing/finalCta";
import StickyCtaBar from "./roofing/StickyCtaBar";
import { SignupModal } from "./roofing/signup";
import { appLoginPath } from "@/lib/landingPaths";

// Logo image URL from user assets
const LOGO_URL = "/images/logo.png";

// VSL thumbnail placeholder
const VSL_THUMBNAIL = "/images/thumbnail_roofing.png";


// Header Component
const Header = ({ onOpenSignup }) => (
  <header className="fixed top-0 left-0 right-0 z-50 bg-graphite-dark/80 backdrop-blur-lg border-b border-white/5">
    <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
      <img 
        src={LOGO_URL} 
        alt="FractoLabs" 
        className="h-6 md:h-7 w-auto"
        data-testid="header-logo"
      />
      <div className="flex items-center gap-4">
        <Link
          to={appLoginPath()}
          className="text-[#9CA3AF] font-body text-sm hover:text-[#F3F4F6] transition-colors"
          data-testid="header-login-link"
        >
          Login
        </Link>
        <button 
          onClick={onOpenSignup}
          className="bg-gradient-to-r from-orange-500 to-red-600 text-white font-heading font-bold text-sm py-2 px-5 rounded-lg hover:from-orange-400 hover:to-red-500 transition-colors duration-300"
          data-testid="header-cta-button"
        >
          {CTA_COPY.headerButton}
        </button>
      </div>
    </div>
  </header>
);

// VSL Video Section
const VSLSection = ({ onOpenSignup }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const YOUTUBE_VIDEO_ID = "dYvjhIAzByo";

  const handlePlayClick = () => {
    setIsPlaying(true);
  };

  return (
    <section className="relative pt-6 pb-16 px-6 radial-gradient-video-roofing" data-testid="vsl-section">
      <div className="max-w-4xl mx-auto">
        {/* Video Container */}
        <div 
          className="relative aspect-video rounded-3xl overflow-hidden glass video-glow-fiery border border-orange-500/30"
          data-testid="video-container"
        >
          {!isPlaying ? (
            <>
              {/* Thumbnail */}
              <img 
                src={VSL_THUMBNAIL} 
                alt="Video thumbnail" 
                className="w-full h-full object-cover"
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors duration-300 cursor-pointer" onClick={handlePlayClick} />
              
              {/* Play Button */}
              <button 
                onClick={handlePlayClick}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 play-btn-pulse w-20 h-20 md:w-24 md:h-24 bg-gradient-to-r from-orange-500 to-red-600 rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300"
                data-testid="play-button"
                aria-label="Play video"
              >
                <Play className="w-8 h-8 md:w-10 md:h-10 text-white ml-1" fill="white" />
              </button>
            </>
          ) : (
            /* YouTube Embedded Video */
            <iframe
              src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&rel=0`}
              title="AI Employee Demo Video"
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              data-testid="youtube-player"
            />
          )}
        </div>

        {/* CTA Block */}
        <div className="mt-10 text-center">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4">
            {/* Hire AI Employee Button */}
            <button
              onClick={onOpenSignup}
              className="btn-pulse-fiery bg-gradient-to-r from-orange-500 to-red-600 text-white font-heading font-bold text-lg md:text-xl py-4 px-8 md:px-12 rounded-lg hover:from-orange-400 hover:to-red-500 transition-colors duration-300"
              data-testid="cta-button-main"
            >
              {CTA_COPY.button}
            </button>
          </div>

          <p className="mt-4 text-[#9CA3AF] text-sm md:text-base">{CTA_COPY.micro}</p>
        </div>
      </div>
    </section>
  );
};

// Footer Section
const Footer = () => (
  <footer className="py-16 px-6 border-t border-white/5 pb-28 md:pb-16" data-testid="footer-section">
    <div className="max-w-4xl mx-auto text-center">
      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-6">
        <a
          href="https://fractolabs.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#9CA3AF] text-sm hover:text-[#F3F4F6] transition-colors"
        >
          FractoLabs
        </a>
        <span className="text-[#9CA3AF]/30 hidden sm:inline">|</span>
        <span className="text-orange-400/90 text-sm font-body">Roofing Interceptor</span>
        <span className="text-[#9CA3AF]/30 hidden sm:inline">|</span>
        <a
          href="https://fractolabs.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#9CA3AF] text-sm hover:text-[#F3F4F6] transition-colors"
        >
          AI Broker
        </a>
      </div>

      <p className="text-[#9CA3AF] text-sm mb-6">
        Questions?{" "}
        <a href="mailto:support@fractolabs.com" className="text-orange-400 hover:underline">
          Contact us
        </a>
      </p>

      <div className="flex flex-wrap items-center justify-center gap-6 mb-4">
        <a
          href="https://fractolabs.com/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#9CA3AF] text-sm hover:text-[#F3F4F6] transition-colors"
          data-testid="privacy-link"
        >
          Privacy Policy
        </a>
        <span className="text-[#9CA3AF]/30">|</span>
        <a
          href="https://fractolabs.com/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#9CA3AF] text-sm hover:text-[#F3F4F6] transition-colors"
          data-testid="terms-link"
        >
          Terms of Service
        </a>
      </div>

      <p className="text-[#9CA3AF]/60 text-xs font-mono">© 2026 FractoLabs. All rights reserved.</p>
    </div>
  </footer>
);

// Landing Page Component
const LandingPageRoofingCompany = () => {
  const [isSignupOpen, setIsSignupOpen] = useState(false);

  const openSignup = () => setIsSignupOpen(true);
  const closeSignup = () => setIsSignupOpen(false);

  return (
    <div className="min-h-screen bg-graphite-dark roofing-landing" data-testid="landing-page">
      <Header onOpenSignup={openSignup} />
      <main className="pb-24 md:pb-0">
        <HeroSection onOpenSignup={openSignup} />
        <CaseStudySection />
        <VSLSection onOpenSignup={openSignup} />
        <EconomicsSection />
        <CustomerExperienceBand />
        <SecretarySection onOpenSignup={openSignup} />
        <EmployeeDutiesSection />
        <ControlSection />
        <GuardrailsSection />
        <FaqSection />
        <FinalCtaSection onOpenSignup={openSignup} />
      </main>
      <Footer />
      <StickyCtaBar onOpenSignup={openSignup} />
      <SignupModal isOpen={isSignupOpen} onClose={closeSignup} />
    </div>
  );
};

export default LandingPageRoofingCompany;
